<template>
  <div class="info-container" v-rect-loading="loading">
    <div class="basic-info">
      <van-image
        class="avatar"
        round
        fit="cover"
        :src="photo || basicProfile.photo"
      />
      <div class="info info-name">
        <span class="name">{{ basicProfile.legal_name }}</span>
        <van-icon class="icon-gender iconfont gender" :class="gender" />
      </div>
      <div class="info info-age">
        <span class="age">{{ age }}</span>
        <span class="phone">{{ basicProfile.phone }}</span>
      </div>
      <div class="info">
        <span class="email-address">{{basicProfile.email}}</span>
      </div>
    </div>
    <div class="common-info address">
      <div class="title">{{ $t(`${i18nPrefix}.label.address`) }}</div>
      <div class="info">
        <van-cell :border="false" :value="address || ''" />
      </div>
    </div>
    <div class="common-info education-info" v-if="education">
      <div class="title">{{ $t(`${i18nPrefix}.label.education`) }}</div>
      <div class="info" v-for="item in education" :key="item.pk">
        <van-cell class="level" :border="false" :value="item.degree_level ? getConstantI18nValue(DEGREE, item.degree_level, 'meta.DEGREE') : ''" />
        <van-cell class="name" :border="false" :value="item.school.i18nName || ''" />
      </div>
    </div>
    <div class="common-info exception-info" v-if="workExperience">
      <div class="title">{{ $t(`${i18nPrefix}.label.experience`) }}</div>
      <div class="info" v-for="item in workExperience" :key="item.pk">
        <van-cell class="industry" :border="false" :value="`${item.company_name || ''} - ${item.job_title}`" />
        <van-cell class="duration" :border="false" :value="`${formatDate(item.start_date) || ''} - ${formatDate(item.end_date) || $t(`${i18nPrefix}.message.endDate`)}` " />
        <van-cell v-if="item.summary" class="summary" :border="false" :value="item.summary || ''" />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty, getConstantI18nValue } from '@/i18n'
import { mapActions } from 'vuex'
import mixin from './mixin'
import { DEGREE } from '@/constant/meta'
import moment from 'moment'

export default {
  mixins: [mixin],
  props: {
    photo: {
    }
  },
  data () {
    return {
      DEGREE,
      getConstantI18nValue,
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      basicProfile: {},
      workExperience: [],
      education: [],
      currentLocation: {}
    }
  },
  computed: {
    age () {
      return this.$t(`${this.i18nPrefix}.label.yearsOld`, { age: moment().diff(this.basicProfile.birthday, 'year') })
    },
    gender () {
      if (this.basicProfile.gender === 'M') {
        return 'iconResume-male'
      }
      if (this.basicProfile.gender === 'F') {
        return 'iconResume-female'
      }
      return ''
    },
    address () {
      const currentLocation = this.currentLocation
      let address = `${currentLocation.countryName || ''}, ${currentLocation.provinceName || ''}, ${currentLocation.cityName || ''}`
      if (this.basicProfile.address) {
        address = `${address}, ${this.basicProfile.address}`
      }
      return address
    },
    companyId () {
      return this.$route.query.company_id
    },
    jobId () {
      return this.$route.query.job_id
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('personal', ['getPersonalProfileInfo']),
    formatDate (date) {
      if (!date) {
        return
      }
      return moment(date).format('YYYY-MM')
    },
    async initData () {
      this.changeLoading(true)
      try {
        const resp = await this.getPersonalProfileInfo({ company_id: this.companyId, job_id: this.jobId })
        if (resp) {
          this.basicProfile = resp.base_info
          this.workExperience = resp.experience.work_experience
          this.education = resp.education
          this.currentLocation = this.basicProfile.current_location
          this.formateData()
        }
      } finally {
        this.changeLoading(false)
      }
    },
    async submit () {
      return true
    },
    formateData () {
      _.forEach(this.education, item => {
        addI18nProperty(item.school, 'i18nName', 'cn_name', 'name')
      })
      addI18nProperty(this.currentLocation, 'countryName', 'country.cn_name', 'country.name')
      addI18nProperty(this.currentLocation, 'provinceName', 'province.cn_name', 'province.name')
      addI18nProperty(this.currentLocation, 'cityName', 'city.cn_name', 'city.name')
    },
    changeLoading (value) {
      this.$emit('load', value)
    }
  }
}
</script>

<style lang="scss" scoped>
.info-container {
  margin: 8px 12px 16px 12px;
  padding-bottom: 24px;
  border-radius: 4px;
  background: #FFFFFF;
  box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.12);
  border: 1px solid #E4E7ED;

  .basic-info {
    text-align: center;
    margin: 0 16px;
    padding: 16px 0;
    border-bottom: 1px solid #DCDFE6;

    .info {
      font-weight: 400;
      font-size: 12px;
      color: #606266;
      line-height: 16px;
      margin-top: 4px;

      .age {
        margin-right: 10px;
      }

      .name {
        font-weight: 500;
        font-size: 14px;
        color: #303133;
        line-height: 20px;
        margin-right: 6px;
        vertical-align: middle;
      }

      .gender {
        vertical-align: middle;
        font-size: 12px;
      }
    }

    .info-name {
      margin-top: 6px;
    }

    .info-age {
      margin-top: 6px;
    }

    .avatar {
      width: 60px;
      height: 60px;
      font-size: 60px;
    }
  }

  .common-info {
    margin: 0 16px;
    padding: 16px 0 0;

    .title {
      font-weight: 500;
      font-size: 16px;
      color: #303133;
      line-height: 22px;
      text-align: left;
      margin-bottom: 12px;
    }

    .info {
      margin-top: 12px;
      white-space: pre-line;
    }

    .van-cell {
      padding: 0;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      .van-cell__value--alone {
        line-height: 20px;
        color: #969799;
      }
    }
  }

  .exception-info {
    .info {
      .industry {
        font-weight: bold;
      }

      .duration {
        font-weight: bold;
      }
    }
  }

  .education-info {
    padding-top: 18px;

    .info {
      .level {
        font-weight: bold;
      }
    }
  }

  .address {
    .info {
      margin: 0;
    }

    .van-cell {
      padding: 0;
      margin-bottom: 0;
    }
  }
}
</style>
